import { BlueButton } from '../../../../../customComponents/buttons/BlueButton'
import { IosShare } from '@mui/icons-material'
import generatePDF, { Margin } from 'react-to-pdf'
import { SurveyType } from '../../../../../../models/SurveySchema'
import { RCNMidYearSummary } from './RCNMidYearSummary'
import { ISDMidYearSummary } from './ISDMidYearSummary'
import { Modal } from '../../../../../customComponents/Modal'
import { type SurveySchema } from '../../../../../../models/SurveySchema'
import { Box, Card, CircularProgress, Stack, Typography, type SxProps, type Theme } from '@mui/material'
import { useState } from 'react'

export interface SurveyElement {
  type: string
  name: string
  title?: string
  text?: string
  variant?: string
  description?: string
  choices?: string[]
  radioMatrix?: Record<string, { hasUpload?: boolean, hasTextInput?: boolean, infoText?: string }> | null
  visibleIf?: string | null
  templateElements?: SurveyElement[]
  conditionalRenderKey?: string | null
}

export interface SurveyPage {
  title: string
  elements: SurveyElement[]
}

export interface SurveyContent {
  title: string
  pages: SurveyPage[]
}

const PDF_ELEMENT_ID = 'midyear-summary-pdf'

const delay = async (ms: number): Promise<void> => {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

const toggleAccordions = (expand: boolean): void => {
  const accordions = document.getElementById(PDF_ELEMENT_ID)?.querySelectorAll('[data-testid^="accordion-"]')
  accordions?.forEach((accordion) => {
    const accordionElement = accordion as HTMLElement
    const summary = accordionElement.querySelector('.MuiAccordionSummary-root')
    const isExpanded = accordionElement.classList.contains('Mui-expanded')
    if ((summary != null) && ((expand && !isExpanded) || (!expand && isExpanded))) {
      (summary as HTMLButtonElement).click()
    }
  })
}

const styles: Record<string, SxProps<Theme>> = {
  container: {
    backgroundColor: 'var(--BaseTones-primitive-nuetral-50, rgba(238, 238, 239, 1))',
    borderRadius: '12px',
    padding: '20px 40px 40px 40px',
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    position: 'relative',
    overflow: 'visible'
  },
  section: {
    backgroundColor: 'var(--BaseTones-primitive-surface-100, rgba(255, 255, 255, 1))',
    padding: '32px 40px 40px 40px',
    borderRadius: '8px',
    border: '1px solid var(--Theme-primitive-primary-300, rgba(84, 153, 195, 1))',
    boxShadow: '0px 2px 7px -1px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08)), 0px 1px 0px 0px var(--BaseTones-primitive-nuetral-900-a8, rgba(37, 36, 38, 0.08))'
  }
}

export const MidYearSummary = ({ initialSurvey }: { initialSurvey: SurveySchema }): JSX.Element => {
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const content: SurveyContent = JSON.parse(initialSurvey.content)
  const answers: Record<string, any> = initialSurvey.answers != null ? JSON.parse(initialSurvey.answers) : {}

  const exportPDF = async (): Promise<void> => {
    const target = document.getElementById(PDF_ELEMENT_ID)
    if (target == null) return

    const originalWidth = target.style.width
    try {
      setIsGeneratingPdf(true)
      toggleAccordions(true)
      target.style.width = '1000px' // Set a fixed width for better PDF formatting
      await delay(1000)
      await generatePDF(() => target, {
        method: 'open',
        resolution: 3, // Increase resolution for better quality
        page: { margin: Margin.MEDIUM, format: 'letter', orientation: 'landscape' },
        overrides: { canvas: { ignoreElements: (el) => el.hasAttribute('data-hide-in-pdf') } }
      })
    } finally {
      target.style.width = originalWidth
      toggleAccordions(false)
      setIsGeneratingPdf(false)
    }
  }

  if (content == null) return <div>No MidYear Summary found</div>

  return (<>
    <Card sx={styles.container} data-testid='midYearSummaryCard'>
      <Stack gap='20px'>
        <Stack direction='row' justifyContent='space-between' alignItems='center'>
          <Typography variant='h1'>Mid-Year Progress Report Submission</Typography>
          <BlueButton
            dataTestId='exportPdfButton'
            onClick={exportPDF}
            endIcon={<IosShare />}
            disabled={isGeneratingPdf}
          >
            Export
          </BlueButton>
        </Stack>

        <div id='midyear-summary-pdf'>
          <Stack sx={styles.section} gap={2}>
            {initialSurvey.id === SurveyType.RCNMidYear
              ? <RCNMidYearSummary content={content} answers={answers} />
              : <ISDMidYearSummary content={content} answers={answers} />}
          </Stack>
        </div>
      </Stack>
    </Card>

    {isGeneratingPdf && (
      <Modal
        data-hide-in-pdf
        open={true}
        title='Generating PDF'
        onConfirm={() => {}}
        onClose={() => {}}
        hideCancelButton={true}
        hideXtoClose={true}
        hideConfirmButton={true}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant='body1' mt={2}>
              Generating PDF, please wait...
            </Typography>
          </Box>
        }
      />
    )}
  </>)
}
